import { FormControl } from '@angular/forms';
import { FormID, Locale } from '@finxone-platform/shared/enums';
import type { CapabilityGrants } from '@finxone-platform/shared/app-sdk';
import { CardFundingType, CardProvider, CardType, PaymentProvider } from './cards.type';
import { OnboardingConfigFlowTypes, OrgCheckTypes } from './common.type';
import { OpenBankingProvider } from './open-banking.type';
import { NominatedAccountStatusEnum } from './default-constants/aisp-gocardless-flow-redirection-config';
import {
  BorderRadiusKeys,
  ButtonHeightSizeOptions,
  TypographyKeys,
} from './default-constants/global-styling.enum';
import { PispDepositFlowRedirectionConfig } from './default-constants/gocardless-pisp-deposit-flow-redirection-config';

import { ClientOnboardingPayload } from './client-onboarding-payload.type';
import { TimeIntervalUnitEnum } from './common.enum';
import {
  AlertPositions,
  AlertTypes,
  DismissalTypeDesktop,
  DismissalTypeMobile,
} from './default-constants/toaster-alerts.type';
import { PACKS } from './default-constants/workflow-constants.type';
import { KycStatusRoutingConfig } from './kyc-routing.types';
import { PageNavigationMap } from './page-id-mapping.types';
import { TransitionPresetType } from './ui-settings.type';
import {
  BaseCssConfig,
  ComponentLabel,
  GridPosition,
  NavBar,
  PasswordFieldConfigurations,
  SupportedDevice,
  SystemZones,
  WidgetLayout,
  ZONETYPEOPTION,
  customDataType,
} from './workflow.type';
import { ConfirmationPreferenceTypes, ZoneConfiguration } from './workflow.v2.type';

export enum WidgetNames {
  // Nested UI Widgets
  DROPDOWN = 'dropdown',
  BUTTON = 'button',
  CARD = 'card',
  // Normal
  SIGNUP = 'signup',
  RECENT_TRANSACTION_LIST_WIDGET = 'recent_transaction_list_widget',
  VERIFICATION_PAGE = 'consent-verification',
  BIRTHDAY_WIDGET = 'birthday_widget',
  NAME_WIDGET = 'name_widget',
  PHONE_WIDGET = 'phone_widget',
  EMAIL_WIDGET = 'email_widget',
  ADDRESS_WIDGET = 'address_widget',
  FORM_ADDRESS_HISTORY_WIDGET = 'form_address_history_widget',
  PASSWORD_WIDGET = 'password_widget',
  IMAGE_WIDGET = 'image_widget',
  LINE_WIDGET = 'line_widget',
  TEXT_WIDGET = 'text_widget',
  IFRAME_WIDGET = 'iframe_widget',
  QUICK_LINKS_WIDGET = 'quick_links_widget',
  RESEND_WIDGET = 'resend_widget',
  LANDING_ACCOUNTS_LISTING = 'landing_accounts_listing',
  ACCOUNTS_CONTEXT_DROPDOWN = 'landing_accounts_context_dropdown',
  CTA_BUTTON_WIDGET = 'cta_button_widget',
  VERIFICATION_DETAIL_VERIFICATION = 'user_profile_confirmation',
  SUMSUB_WIDGET = 'sumsub_widget',
  AIRWALLEX_RFI_WIDGET = 'airwallex_rfi_widget',
  AIRWALLEX_KYC_WIDGET = 'airwallex_kyc_widget',
  USER_DETAIL_WIDGET = 'user_detail_widget',
  CONTACT_DETAIL_WIDGET = 'contact_detail_widget',
  IMPORT_USER_WIDGET = 'import_user_widget',
  ACCOUNT_LIST_WIDGET = 'account_list_widget',
  MULTI_ROLE_WIDGET = 'multi_role_widget',
  BACK_BUTTON_WIDGET = 'back_button_widget',
  FORWARD_BUTTON_WIDGET = 'forward_button_widget',
  ADD_BENEFICIARY_WIDGET = 'add_beneficiary_widget',
  ADD_BENEFICIARY_FASTER_PAYMENTS = 'add_beneficiary_faster_payments_widget',
  LOGOUT = 'logout',
  SWITCH_NAVIGATOR_QUICK_LINK = 'switch_navigator_quick_link',
  BENEFICIARY_SELECTION_WIDGET = 'beneficiary_selection_widget',
  MULTI_ICON_QUICK_LINK_WIDGET = 'multi_icon_quick_link_widget',
  LOTTIE_ANIMATION = 'lottie_animation',
  CURRENCY_INPUT_FIELD_WIDGET = 'currency_input_field_widget',
  PAYMENT_REF_WIDGET = 'payment_ref_widget',
  MANDATORY_CTA_BUTTON = 'mandatory_cta_button',
  PROFILE_PICTURE_WIDGET = 'profile_picture_widget',
  BIOMETRICS_AUTHENTICATION_WIDGET = 'biometrics_authentication_widget',
  NOTIFICATION_BELL_WIDGET = 'notification_bell_widget',
  NOTIFICATION_WIDGET = 'notification_widget',
  NOTIFICATION_LIST_WIDGET = 'notification_list_widget',
  EXCHANGE_CURRENCY_WIDGET = 'exchange_currency_widget',
  EXCHANGE_QUOTE_WIDGET = 'exchange_quote_widget',
  PASSWORD_CHANGE = 'password_change',
  PASSWORD_FORGOT = 'password_forgot',
  PASSWORD_ENTER_EMAIL = 'password_enter_email',
  PASSWORD_UPDATE = 'password_update',
  INTL_BENEFICIARY_ADDRESS = 'intl_beneficiary_address',
  INTL_BENEFICIARY_ADDED = 'intl_beneficiary_added',
  CURRENCY_ACCOUNTS_LISTING_WIDGET = 'currency_accounts_listing_widget',
  ADD_CURRENCY_ACCOUNT_WIDGET = 'add_currency_account_widget',
  EXCHANGE_QUOTE_SUMMARY = 'exchange_quote_summary',
  INTERNATIONAL_CURRENCY_COUNTRY = 'international_currency_country',
  FILE_UPLOAD_WIDGET = 'file_upload_widget',
  INTERNATIONAL_BENEFICIARY_CREDENTIALS_WIDGET = 'international_beneficiary_credentials_widget',
  INTERNATIONAL_BENEFICIARY_CREDENTIALS = 'international_beneficiary_credentials',
  VERIFICATION_ORGANISATION_COMPANY_NAME = 'verification_organisation_company_name',
  RANGE_CALENDAR_WIDGET = 'range_calendar_widget',
  EDIT_ADDRESS_WIDGET = 'edit_address_widget',
  OTP_VERIFICATION = 'otp_verification_widget',
  FORM_SUBMISSION_BUTTON = 'form_submission_button_widget',
  ACTION_BUTTON = 'action_button_widget',
  PAYMENT_REASON_WIDGET = 'payment_reason_widget',
  EDIT_BENEFICIARY_INTERNATIONAL_CURRENCY_COUNTRY = 'edit_beneficiary_international_currency_country',
  EDIT_INTERNATIONAL_BENEFICIARY_CREDENTIALS_WIDGET = 'edit_international_beneficiary_credentials_widget',
  EDIT_INTL_BENEFICIARY_ADDRESS = 'edit_intl_beneficiary_address',
  EDIT_BENEFICIARY_WIDGET = 'edit_beneficiary_widget',
  EDIT_BENEFICIARY_ADDRESS_WIDGET = 'edit_beneficiary_address_widget',
  BULK__PAYMENT_ACCOUNTS_LISTING_WIDGET = 'bulk_payment_accounts_listing_widget',
  DESKTOP_BULK_PAYMENT_LISTING_WIDGET = 'desktop_bulk_payment_listing_widget',
  DESKTOP_BULK_PAYMENT_HISTORY_LISTING_WIDGET = 'desktop_bulk_payment_history_listing_widget',
  CONDITIONAL_WIDGET = 'conditional_widget',
  CONDITIONAL_WIDGET_IF_ELSE_CONFIG = 'conditional_widget_config',
  ONBOARDING_BACK_NEXT_BUTTON = 'onboarding_back_next_button',
  UPLOAD_FILE_WITH_POPUP_WIDGET = 'upload_file_with_popup',
  COPY_SHARE_CONTENT = 'copy_share_content',
  TRANSACTION_BUTTON = 'transaction-button',
  TRANSACTION_LIST = 'transaction_list',
  CARDS_WIDGET = 'cards_widget',
  FREEZE_CARD_TOGGLE_SWITCH = 'freeze-card-toggle-switch-widget',
  SECTION = 'section-widget',
  TWO_COLUMN = 'two-column-widget',
  SUB_CONTAINER = 'sub-container',
  TAB = 'tab-widget',
  SPACER = 'spacer-widget',
  CARD_SENSITIVE_DETAILS = 'card-sensitive-details',
  WIDGET_NESTED_BUTTON = 'widget-nested-button',
  TIMER_BUTTON = 'timer-button',
  DESKTOP_PAYMENT_REQUEST_PENDING_APPROVED_LISTING_WIDGET = 'desktop-payment-request-pending-approved-listing-widget',
  SECONDARY_IDENTIFIER = 'secondary-identifier',
  CARD_NAME_WIDGET = 'card-name-widget',
  CARD_PIN = 'card-pin',
  ADD_TO_GOOGLE_PAY_BUTTON = 'add-to-google-pay',
  ADD_TO_APPLE_WALLET_BUTTON = 'add-to-apple-wallet',
  PAYMENT_RAIL_SELECTOR_WIDGET = 'payment-rail-selector-widget',
  ADD_BENEFICIARY_INTL_WIDGET = 'add_beneficiary_intl_widget',
  THEME_SELECTOR = 'theme_selector_widget',
  EDIT_BENEFICIARY_INTL_NEW_CURRENCY_COUNTRY = 'edit_beneficiary_intl_new_currency_country',
  EDIT_BENEFICIARY_INTL_WIDGET = 'edit_beneficiary_intl_widget',
  CURRENCY_SELECTOR_WIDGET = 'currency_selector_widget',
  FORM_WIDGET = 'form_widget',
  INPUT_WIDGET = 'input_widget',
  FORM_BUTTON_WIDGET = 'form_button_widget',
  VERIFY_INPUT_FIELD_BUTTON = 'verify_input_field_button',
  DISPLAY_DOCUMENT_WIDGET = 'display-document-widget',
  BENEFICIARY_TYPE_WIDGET = 'beneficiary-type-widget',
  BENEFICIARY_COUNTRY_CURRENCY_WIDGET = 'beneficiary-country-currency-widget',
  BENEFICIARY_PAYMENT_RAIL_WIDGET = 'beneficiary-payment-rail-widget',
  BENEFICIARY_CREDENTIALS_WIDGET = 'beneficiary-credentials-widget',
  BENEFICIARY_ADDRESS_WIDGET = 'beneficiary-address-widget',
  CHART_WIDGET = 'chart-widget',
  BANK_LISTING_WIDGET = 'bank-listing-widget',
  ACCOUNT_SELECTION_WIDGET = 'account-selection-widget',
  ACCOUNT_REQUEST_LIST_WIDGET = 'account-request-list-widget',
  FORM_NAME_WIDGET = 'form_name_widget',
  FORM_BIRTHDAY_WIDGET = 'form_birthday_widget',
  FORM_ADDRESS_WIDGET = 'form_address_widget',
  ONBOARDING_BUTTON = 'onboarding_button',
  FORM_PASSWORD_WIDGET = 'form_password_widget',
  DOCUMENT_LISTING_WIDGET = 'document-listing-widget',
  MULTI_CURRENCY_WIDGET = 'multi_currency_widget',
  PAYMENT_CANCELLATION_WIDGET = 'payment-cancellation-widget',
  CANCEL_PAYMENTS_INFO_WIDGET = 'cancel-payments-info-widget',
  CLOUDFLARE_TURNSTILE_WIDGET = 'cloudflare_turnstile_widget',
  GENERIC_CAROUSEL_WIDGET = 'generic-carousel',
  GOCARDLESS_COMPLIANCE_WIDGET = 'gocardless-compliance-widget',
  PISP_BANK_LIST_WIDGET = 'pisp-bank-list-widget',
  PISP_CHECK_BILLING_REQUEST_LOADER = 'pisp-check-billing-request-loader',
  SUPPORT_CHAT_LIST_WIDGET = 'support-chat-list-widget',
  SUPPORT_CHAT_WIDGET = 'support-chat-widget',
  ACCORDION_WIDGET = 'accordion-widget',
  PRODUCTS_LISTING_WIDGET = 'products-listing-widget',
  TOOLTIP_WIDGET = 'tooltip-widget',
  COMPANIES_HOUSE_SEARCH_WIDGET = 'companies-house-search-widget',
  COMPANIES_HOUSE_WIDGET_NESTED_SEARCH_BUTTON = 'companies-house-widget-nested-search-button',
  COMPANIES_HOUSE_LISTING_WIDGET = 'companies-house-listing-widget',
  COMPANIES_HOUSE_DIRECTOR_SELECTION_WIDGET = 'companies-house-director-selection-widget',
  AUTHENTICATION_FLOW_WIDGET = 'authentication-flow-widget',
  VARIABLE_RECURRING_PAYMENT_LIST_WIDGET = 'variable-recurring-payment-list',
  CUSTOM_WIDGET = 'custom_widget',
}

export enum LayoutNames {
  FOOTER_LAYOUT = 'footer-layout',
  PAGE_CONTENT = 'page-content',
}

export enum SectionNames {
  MANAGE_USER_PROPERTY_SECTION = 'manage_user_property_section',
  ADD_USER_WIDGET_SECTION = 'detail',
  MANAGE_USER_WIDGET_SECTION = 'list',
}
export enum RoleStatus {
  NEW = 'new',
  UPDATE = 'update',
  DELETE = 'deleted',
  NOUPDATE = 'no-updates',
}

export enum ActiveDevice {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export enum DeviceZoomLevel {
  MOBILE = 11,
  TABLET = 9,
  DESKTOP = 7,
}

export enum WidgetTypes {
  SIGNUP = 'signup_widget',
  QUESTION = 'question_widget',
}

export enum VerificationStatus {
  PASS = 'Pass',
  FAILED = 'Failed',
}

export enum ProjectConfigOnboardingFlow {
  BUSINESS_FIRST = 'business_first',
  PERSONAL_FIRST = 'personal_first',
}

export enum BaseCssProperties {
  V_ALIGN_DISPLAY_BLOCK = 'vertical-align-display-block',
  MARGIN = 'margin',
  PADDING = 'padding',
  COLOR = 'color',
  TEXTALIGN = 'text-align',
  BORDER = 'border',
  BACKGROUND_COLOR = 'background-color',
  V_ALIGN = 'vertical-align',
  DISABLE_BACKGROUND_COLOR = 'disable-background-color',
  DISABLE_COLOR = 'disable-color',
  OBJECT_FIT = 'object-fit',
  FONT_SIZE = 'font-size',
  TEXT_BOLD = 'text-bold',
  TEXT_ITALIC = 'text-italic',
  TEXT_UNDERLINE = 'text-underline',
  TEXT_FONT_STYLE = 'text-font-style',
  BOX_SHADOW = 'box-shadow',
  SELECTED_BORDER = 'selected-border',
  FONT_WEIGHT = 'font-weight',
  TEXT_INPUT_FONT_SIZE = 'text-input-font-size',
  TEXT_INPUT_BG_COLOR = 'text-input-bg-color',
  ICON_SIZE = 'icon-size',
  DIMENSIONS = 'dimensions',
  DISABLED_BORDER_COLOR = 'disabled-border',
  HEIGHT_WIDTH = 'height-width',
  INPUT_BORDER = 'input-border',
  INPUT_IDLE_BACKGROUND_COLOR = 'input-idle-background-color',
  INPUT_IDLE_BORDER_COLOR = 'input-idle-border-color',
  INPUT_ACTIVE_BACKGROUND_COLOR = 'input-active-background-color',
  ICON_COLOR = 'icon-color',
  RIGHT_ICON_SIZE = 'right-icon-size',
  RIGHT_ICON_COLOR = 'right-icon-color',
  LEFT_ICON_SIZE = 'left-icon-size',
  LEFT_ICON_COLOR = 'left-icon-color',
  MIN_WIDTH = 'min-width',
  TITLE_FONT = 'title-font',
  DESCRIPTION_FONT = ' description-font',
  ACTIVE_BACKGROUND_COLOR = 'active-background-color',
  IN_ACTIVE_BACKGROUND_COLOR = 'in-active-background-color',
  ACTIVE_BORDER_COLOR = 'active-border-color',
  IN_ACTIVE_BORDER_COLOR = 'in-active-border-color',
  ALERT_LEFT_ICON = 'alert-left-icon',
  ALERT_RIGHT_ICON = 'alert-right-icon',
  ALERT_BORDER = 'alert-border',
  BORDER_V2 = 'borderV2',
  COLORS_V2 = 'colorsV2',
}

export const commonContentStyleList = [
  BaseCssProperties.MARGIN,
  BaseCssProperties.PADDING,
  BaseCssProperties.BORDER,
  BaseCssProperties.COLOR,
  BaseCssProperties.TEXTALIGN,
  BaseCssProperties.BACKGROUND_COLOR,
  BaseCssProperties.V_ALIGN,
  BaseCssProperties.BOX_SHADOW,
];

export const buttonNestedWidgetConfigStyles = [
  BaseCssProperties.MARGIN,
  BaseCssProperties.PADDING,
  BaseCssProperties.BORDER,
  BaseCssProperties.BOX_SHADOW,
  BaseCssProperties.COLOR,
  BaseCssProperties.BACKGROUND_COLOR,
  BaseCssProperties.FONT_SIZE,
  BaseCssProperties.FONT_WEIGHT,
  BaseCssProperties.TEXT_BOLD,
  BaseCssProperties.TEXT_FONT_STYLE,
  BaseCssProperties.TEXT_ITALIC,
  BaseCssProperties.TEXT_UNDERLINE,
  BaseCssProperties.V_ALIGN,
  BaseCssProperties.TEXTALIGN,
];

export const cardNestedWidgetConfigStyles = [
  BaseCssProperties.MARGIN,
  BaseCssProperties.PADDING,
  BaseCssProperties.BORDER,
  BaseCssProperties.BOX_SHADOW,
  BaseCssProperties.COLOR,
  BaseCssProperties.HEIGHT_WIDTH,
  BaseCssProperties.BACKGROUND_COLOR,
  BaseCssProperties.OBJECT_FIT,
];
export const commonTextContentStyleList = [
  BaseCssProperties.COLOR,
  BaseCssProperties.FONT_SIZE,
  BaseCssProperties.TEXT_BOLD,
  BaseCssProperties.TEXT_ITALIC,
  BaseCssProperties.TEXT_UNDERLINE,
  BaseCssProperties.TEXT_FONT_STYLE,
];

export enum WidgetPropertiesType {
  HEADER_CONFIG = 'headerConfig',
  SUBHEADER_CONFIG = 'subheaderConfig',
  TABLE_HEADER_CONFIG = 'tableHeaderConfig',
  TABLE_GRID_SETTING = 'tableGridSetting',
  TABLE_ROW_SETTING = 'tableRowSetting',
}

export enum ValidationRulesType {
  REQUIREDVALIDATION = 'requiredvalidation',
}
export enum integratedFinanceSanboxMode {
  Live = 'live',
  Test = 'test',
}

export enum integratedFinancePricingMode {
  Explicit = 'explicit',
  Implicit = 'implicit',
}

export enum ImageType {
  ALL = '',
  IMAGE = 'image',
  LOGO = 'logo',
  ICON = 'icon',
}

export enum PopupSettingType {
  IMAGE_LIBRARY = 'image_library',
  IMAGE_WIDGET = 'image_widget',
  PAGE_BACKGROUND = 'page_background',
  LOGO_WIDGET = 'logo_widget',
  CARD = 'card',
  ICON_LIBRARY = 'icon_library',
}

export type PopupSettings = {
  show: boolean;
  type: string;
  data?: string;
};

export enum PopupButtonType {
  CANCEL = 'cancel',
  CLOSE = 'close',
}

export type ThemeConfig = {
  additional_css?: string;
  fontFamily: string;
  logo: string;
  logo_title?: string;
  secondaryFontFamily?: string;
};

export type ProfilePageAttributes = {
  css_config: BaseCssConfig;
  additional_configs: {
    additional_fields: ExtraAttributeField[];
    validators: [];
  };
  'terms-and-condition': boolean;
  'privacy-policy': boolean;
};

export type VerificationPage = {
  organisations: {
    individual: {
      css_config: BaseCssConfig;
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
      kycVerficationEmail: boolean;
      kycVerficationPhone: boolean;
    };
  };
};

export type PaymentPage = {
  organisations: {
    individual: {
      addingABeneficiary: boolean;
      additional_configs: {
        additional_fields: [];
        validators: [];
      };
      removingABeneficiary: boolean;
    };
  };
};

export type QuestionWidgetPage = {
  organisations: {
    individual: {
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
      layout: LayoutWidgetConfig[];
    };
  };
};

export type ImageWidgetPage = {
  organisations: {
    individual: {
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
      layout: LayoutWidgetConfig[];
    };
  };
};

export type TextItemWidgetPage = {
  organisations: {
    individual: {
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
      layout: LayoutWidgetConfig[];
    };
  };
};

export type UserPropertiesTable = {
  organisations: {
    individual: {
      css_config: CssConfig;
      columns: ColumnConfig[];
    };
  };
};

export type ColumnConfig = {
  [key: string]: {
    label: string;
    value: string;
    selected: boolean;
    position: number;
  };
};

export type CssConfig = {
  paddingTop: number;
  paddingLeft: number;
  paddingRight: number;
  paddingBottom: number;
  paddingUnit: string;
  marginTop: number;
  marginLeft: number;
  marginRight: number;
  marginBottom: number;
  marginUnit: string;
  textColor: string;
};

export type SystemRole = {
  id: string;
  title: string;
  slug: string;
  prevValue?: string;
  status?: RoleStatus;
  userType?: string;
  alternativeThemes: { value: string; isPrimary: boolean }[];
};

export function getPrimaryTheme(themes?: { value: string; isPrimary: boolean }[]): string | undefined {
  const primary = themes?.find((theme) => theme.isPrimary);

  return primary?.value ?? undefined;
}

export type SystemRoles = {
  [key: string]: SystemRole;
};

export type SystemSubscriptionPlan = {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type SystemSystemConfig = {
  systemName: string;
  legalEntityName: string;
  theme: ThemeConfig;
  alternativeThemes?: {
    [key: string]: AlternativeSystemSystemConfig;
  };
  loginPageConfig: {
    passwordFieldConfig?: PasswordFieldConfigurations;
    logoEnabled: boolean;
  };
};

export type AlternativeSystemSystemConfig = Omit<SystemSystemConfig, 'alternativeThemes'>;

export type IconConfigType = {
  name: string;
  size: string;
  color: string;
  unit: string;
};
export interface AlertStyleType extends Partial<BaseCssConfig> {
  minWidth: number;
  minWidthUnit: string;
  backgroundOpacity: number;
  borderWidthTop?: number;
  borderWidthBottom?: number;
  borderWidthLeft?: number;
  borderWidthRight?: number;
  borderWidthUnit?: string;
  textStyle: {
    titleFontSize: number;
    titleFontFamily: string;
    descriptionFontSize: number;
    descriptionFontFamily: string;
    descriptionColor: string;
    titleColor: string;
  };
  iconConfiguration: { [iconType: string]: IconConfigType };
}

export type AlertBehaviourSettings = {
  dismissalType: DismissalTypeDesktop | DismissalTypeMobile;
  dismissAfter: string;
  position: AlertPositions;
};

export type AlertItemConfigurationType = {
  behaviourSettings: AlertBehaviourSettings;
  styles: AlertStyleType;
  htmlString?: string;
};

export type AlertStyleConfigurationType = {
  [device in ActiveDevice]?: {
    [type in AlertTypes]: AlertItemConfigurationType;
  };
};

export type UiSystemConfig = {
  system: SystemSystemConfig;
  zones: SystemZones;
  navbar: { [key: string]: NavBar };
  customData: customDataType;
  alertsConfig?: AlertStyleConfigurationType;
  pageUrlToPageIdMap?: { [device: string]: { [pageUrl: string]: string } };
};
export type GetSystemConfig = {
  config_key: string;
  config_value: SystemConfiguration;
};

export interface ProviderConfigInterface {
  currency: string;
  checkPayeeEnabled: boolean;
  smsAuthenticationEnabled: boolean;
}

export type AccountProviderService = {
  fullBalanceWithdrawalValidation?: boolean;
  provider:
    | 'integratedFinance'
    | 'modulr'
    | 'railsr'
    | 'andaria'
    | 'universal-sandbox'
    | 'thisbank'
    | 'saascada'
    | 'equalsmoney'
    | 'airwallex';
  /** Provisioner-owned Data Studio catalogue and per-widget access grants. */
  widgetStorage?: {
    revision: number;
    definitions: Record<
      string,
      {
        definitionId: string;
        version: number;
        scope: 'user' | 'organisation';
        description: string;
        schema: Record<string, unknown>;
        schemaHash: string;
        migrations: Array<{ fromVersion: number; toVersion: number; steps: unknown[] }>;
        status: 'available' | 'deprecated';
        /**
         * ADR 0022 decision 5. `document` (the default when absent) is one record per
         * definition per subject, served by `records.*`; `collection` is many records,
         * served by `records.items.*`, queried through a bounded AST against `indexes`
         * and refused unless one of `requiredQueryFilters` is satisfied.
         */
        shape?: DataDefinitionShape;
        indexes?: DataCollectionIndex[];
        requiredQueryFilters?: string[][];
      }
    >;
    access: Record<string, { read: string[]; write: string[] }>;
  };
  /**
   * Provisioner-owned projection of the project's activated backend functions and
   * the outbound connections they may use. Dispatchers read this instead of
   * calling the provisioner on the request path (ADR-13), so it carries the
   * artifact hash the executor re-verifies against.
   *
   * A structural duplicate of the manifest/connection types in
   * `@finxone-platform/shared/backend-sdk` — deliberately, so this lib stays
   * dependency-free. Keep the two in step.
   *
   * Secret VALUES never appear here (nor in any registry row). Only names are
   * recorded; they are resolved on the functions-host from environment keys
   * derived as `FN_SECRET_<NAME>` for a function's own secrets and
   * `FN_CONN_<SLUG_UC>__API_KEY` / `__CLIENT_ID` / `__CLIENT_SECRET` for a
   * connection's credentials (see `requiredSecretKeys`).
   */
  functions?: {
    revision: number;
    functions: Record<
      string,
      {
        functionId: string;
        version: number;
        artifactSha256: string;
        artifactFileId: string;
        trigger: string;
        /**
         * Which platform event fires this function, version included
         * (`payment.initiated@1`). Present only when `trigger` is `event`, where it
         * is the whole binding — the dispatcher fans one event out to every entry
         * naming it, in slug order.
         */
        event?: string;
        /**
         * The five-field UTC cron expression that fires this function. Present
         * only when `trigger` is `schedule`, where — like `event` — it IS the
         * binding: the dispatcher reconciles one pg-boss schedule per entry
         * carrying one, and removes any schedule no entry claims.
         */
        schedule?: string;
        /**
         * The unguessable segment of this function's inbound webhook URL, minted
         * once per activation. Present only when `trigger` is `webhook`. The
         * signing secret is NOT here and is not projected anywhere: it is a value,
         * so it takes the namespace secret channel as `FN_WEBHOOK_<SLUG_UC>` like
         * every other credential.
         */
        webhookNonce?: string;
        /**
         * When this function fires in a frontend, and on which routes. Present
         * only when `trigger` is `frontend-lifecycle`, where — like `event` and
         * `schedule` — it IS the binding: account-service publishes it on
         * `GET /app/.well-known/finxone-app` and the App SDK's
         * `installFrontendHooks` dispatches from it. Nothing server-side reads it.
         */
        lifecycle?: { on: string[]; routes?: string[] };
        /**
         * The App SDK operations this function must approve before a frontend
         * sends them. Present only when `trigger` is `guard-hook`. Published on
         * the well-known document for the same reason `lifecycle` is; the
         * frontend half is ADVISORY — the functions-host guard path is the
         * enforcement.
         */
        guards?: { operations: string[] };
        displayName: string;
        description?: string;
        auth: { requires: 'user' | 'none'; allowedRoles?: string[] };
        input: Record<string, unknown>;
        output: Record<string, unknown>;
        grants: {
          connections: string[];
          egress: string[];
          capabilities: string[];
          secrets: string[];
          /** Templates `notifications.send` may name; enforced on the functions-host. */
          notificationTemplates?: string[];
          /**
           * External identifier systems `ctx.tenant.resolve` may name, on a
           * webhook-triggered function. Enforced on the functions-host, which is
           * also where the resolved organisation is held — it is never projected
           * and never reaches the worker.
           */
          tenantSystems?: string[];
          /**
           * Whether `ctx.tenants.list` and `ctx.tenant.use` are granted, on a
           * schedule-triggered function. Enforced on the functions-host, which is
           * also where the refs are minted and where the organisation each one
           * stands for is held — neither is ever projected and neither reaches the
           * worker.
           */
          tenantIteration?: boolean;
          /**
           * Data-definition references the `records.*` capabilities may touch,
           * split by access — the function-side twin of `widgetStorage.access`,
           * and read the same way (by slug here, by widget name there) so that
           * account-service resolves both through one code path.
           */
          storage?: { read: string[]; write: string[] };
        };
        timeoutMs: number;
        memoryMb?: number;
      }
    >;
    connections: Record<
      string,
      {
        authMode: 'none' | 'api-key' | 'client-credentials';
        baseOrigin: string;
        tokenUrl?: string;
        scopes?: string[];
        authScheme?: 'bearer' | 'x-api-key' | 'header';
        headerName?: string;
        defaultHeaders?: Record<string, string>;
      }
    >;
  };
  maxEnrolledDevices?: number;
  cardsConfig?: CardConfigValuesType;
  exchangeConfig?: ExchangeConfigValuesType;
  paymentRequest?: accountServicePaymentRequest;
  modulrConfig?: {
    enabled?: boolean;
    currency: string;
    applicant: boolean;
    tcsVersion: number;
    sandboxConfig: {
      sortCode: string;
      customerId: string;
      fundAmount: number;
      sourceAccountId: string;
      addFundEnabled: boolean;
    };
    supportedCurrency: string[];
    sandboxEnabled: boolean;
    virtualCardAmount: number;
    virtualCardExpiry: number;
    accountProductCode: string;
    expectedMonthlySpend: number;
    virtualCardProductCode: string;
    physicalCardProductCode: string;
    physicalCardAmount: number;
    physicalCardExpiry: number;
    checkPayeeEnabled: boolean;
    smsAuthenticationEnabled: boolean;
    smsAuthenticationExclusion: {
      [key: string]: string[];
    };
  };
  operationalAccounts?: {
    [key: string]: {
      feeCollectionAccountId?: string;
      feeCollectionClientId?: string;
    };
  };
  termDeposit?: {
    provider: 'integratedFinance' | 'universal-sandbox';
    fixedDepositPercentageFee?: number;
    variableDepositPercentageFee?: number;
  };
  // Savings nominated-account AIS linking. Defaults to gocardless with no config; automatically
  // uses universal-sandbox when that's the main account_service.provider; explicit override wins
  // over both (see CommonService.getOpenBankingProvider).
  openBankingConfig?: {
    provider: OpenBankingProvider;
  };
  integratedFinanceConfig?: IntegratedFinaceConfigValuesType;
  andariaConfig?: AndariaConfig;
  equalsConfig?: EqualsConfig;
  airwallexConfig?: AirwallexConfig;
  embedConfig?: EmbedConfig;
  universalSandboxConfig?: UniversalSandboxConfigValuesType;
  thisbankConfig?: ThisBankConfigValuesType;
  saascadaConfig?: SaascadaConfigValuesType;
  enableBeneficiaryFirstAndLastName?: boolean;
  railsrConfig?: RailsrConfigValuesType;

  supportIndividualClients?: boolean;
  supportCorporateClients?: boolean;
  kycProvider?: string;
  directClientCreation?: boolean;

  savings?: {
    nominatedAccount?: {
      sandboxEnabledManyAccountsResponseOnProcessing?: boolean;
    };
    kyc: {
      kycAccountOpen?: boolean;
      accountOpenApplicantKycFlows: {
        provider: 'sumsub';
        sumsubConfig: {
          defaultFlow: string;
          flows: {
            [productType: string]: {
              [productCode: string]: string;
            };
          };
        };
      };
    };
  };
};

export type UniversalSandboxConfigValuesType = {
  currency: string;
  tenantId: string;
};

export type ThisBankConfigValuesType = {
  onboarding: {
    mandatoryExtraAttributesInputsValidation: string[];
  };
};

export type SaascadaConfigValuesType = {
  baseUrl: string;
  institutionContextId?: string;
  enableNominatedAccountAutoSetup?: boolean;
  checkPayeeProvider?: 'snaplogic' | 'saascada';
  // Projects that CoP-verify a nominated account during onboarding (DF Capital): the AISP link
  // afterwards must resolve to that same account, or it is rejected rather than saved.
  enforceOnboardingNominatedAccountMatch?: boolean;
};

export type AndariaConfig = {
  currency: string;
  useMultiCurrency?: boolean;
  alternativeBackends?: {
    [key: string]: AndariaConfig;
  };
  checkPayeeEnabled: boolean;
  smsAuthenticationEnabled: boolean;
};

export type AirwallexOtpDeliveryMethod = 'email' | 'sms' | 'cardholder_selected';

export type AirwallexConfig = {
  enabled?: boolean;
  baseUrl?: string;
  filesBaseUrl?: string;
  webhook?: {
    enabled?: boolean;
    sharedSecretSecretName?: string;
  };
  firstFormations?: {
    webhookSecretSecretName?: string;
  };
  /**
   * When true the platform owns cardholder issuing notifications (delegation
   * mode). When false Airwallex delivers them and we persist the webhooks only.
   */
  whiteLabelNotifications?: boolean;
  /** 3DS step-up OTP channel. `cardholder_selected` follows the webhook. */
  threeDsOtpDelivery?: AirwallexOtpDeliveryMethod;
  /** Digital-wallet provisioning OTP channel. */
  walletOtpDelivery?: AirwallexOtpDeliveryMethod;
  /**
   * When true the embedded `kyc` component collects identity and submits the connected account,
   * so the backend skips POST /api/v1/accounts/{id}/submit. Requires Airwallex to have enabled
   * embedded components on the platform account.
   */
  embeddedOnboarding?: boolean;
};

export type EqualsConfig = {
  enabled?: boolean;
  baseUrl?: string;
  marqetaJsUrl?: string;
  market?: 'UK' | 'EU' | 'US';
  defaultAccountFeatures?: string[];
  m2m?: {
    allowedClientIds?: string[];
  };
  webhook?: {
    enabled: boolean;
    eventTypes?: string[];
    sharedSecretSecretName?: string;
  };
  onboarding?: {
    mode: 'delegated' | 'hybrid' | 'managed';
    directClientCreation?: boolean;
    createCorporateInitialUserByDefault?: boolean;
    assignOwnerRoleToCorporateInitialUser?: boolean;
    defaultInitialUserPersonas?: {
      individual?: string[];
      corporate?: string[];
    };
    defaultInitialUserRoles?: {
      individual?: string[];
      corporate?: string[];
    };
  };
  cards?: {
    enabled: boolean;
    defaultOwnerType?: 'BUDGET' | 'PEOPLE';
  };
};

/**
 * One widget a partner may embed in their own application, keyed by widget slug
 * under `embedConfig.widgets`. `version` pins the immutable bundle directory the
 * embed runtime loads from; `grants` is the same capability set the in-app
 * activation carries, enforced again server-side by the embed BFF.
 */
export type EmbedWidgetConfig = {
  version: string;
  /** Per-activation real-origin namespace; never treated as an authorisation secret. */
  originKey?: string;
  grants: CapabilityGrants;
  settings?: Record<string, unknown>;
  defaultHeight?: number;
};

/**
 * Embedded widget runtime ("reverse BYOW"). Absent — or with an empty
 * `m2m.allowedClientIds` — embedding is off: the token API rejects every caller.
 */
export type EmbedConfig = {
  m2m?: {
    allowedClientIds: string[];
  };
  widgets?: Record<string, EmbedWidgetConfig>;
  /** Design tokens delivered to embedded widgets as `context.theme`. */
  theme?: Record<string, string>;
};

export type CardConfigValuesType = {
  enabled: boolean;
  provider: CardProvider;
  integratedFinanceConfig?: {
    serviceProvider: 'thredd';
  };
  // eslint-disable-next-line @typescript-eslint/ban-types
  railsrConfig?: {};
  // json object with a unique key for each card type available
  cardOptions: CardOptionsConfig;
};

export type CardOptionsConfig = {
  [cardProductIdentifier: string]: CardConfig;
};

export type FeeManagementServiceType = {
  enabled: boolean;
  retryFailedFeeEnabled: boolean;
  feeReversalEnabled: boolean;
};

export type ExchangeConfigValuesType = {
  enabled: boolean;
  provider: 'integratedFinance' | 'module' | 'railsr' | 'finxoneUnit';
};

export type CardConfig = {
  // alias is name of card may or may not be applicable depending on provider
  defaultAlias?: string;
  // virtual of physical card
  type: CardType | string;
  // threddAttributes only applicable on integratedFinance using thredd serviceProvider
  threddAttributes?: {
    threddCardProduct: string;
    threddDesignId: string;
    threddImageId?: string;
    threddCardManufacturer?: string;
    threddDeliveryMethod?: string;
    threddCarrierType?: string;
  };
  railsrAttributes?: {
    uk_card_delivery_method?: 'standard-first-class';
    card_programme:
      | 'Railsbank-Debit-Card-2'
      | 'Railsbank-Debit-Card-3'
      | 'Railsbank-Credit-Card-1'
      | 'Railsbank-Credit-Card-2';
    card_design: string;
    card_rules?: string[];
  };
  designID?: string;
  fundingType?: CardFundingType | string;
  // more optional attribtes depending on the card integration...
};

export type NotificationProviderService = {
  provider: string;
  profile_notification_duplicate_delay: number;
  // Additional Novu tenant keys (e.g. 'gfc'), each resolved against <KEY>_NOVU_TENANT
  // in the target deployment; the default NOVU_TENANT is always available
  tenants?: string[];
};

export type ProviderLimitConfig = {
  perCurrencyLimit?: number;
  principalAccountLimit?: number;
  currency: {
    [key: string]: number;
  };
};

export type RailsrConfigValuesType = {
  enabled?: boolean;
  currency: string;
  currencyalias: string;
  currencyProduct: {
    [key: string]: string;
  };
  supportedCurrency: string[];
  debitOnSettlementExchangeEnabled?: boolean;
  enableHeaders: boolean;
  serviceProvider?: string;
  checkPayeeEnabled: boolean;
  disableIndividualAutomaticAccountCreation?: boolean;
  providerCurrencyAccountLimit?: ProviderLimitConfig;
};

export type IntegratedFinaceConfigValuesType = {
  enabled?: boolean;
  currency: string;
  currencyalias: string;
  accountClosureOnZeroBalance?: boolean;
  accountClosureNoticePeriod?: {
    days?: number;
    hours?: number;
    minutes?: number;
  };
  customPaymentReasons?: string[];
  sandboxConfig: {
    clientId: string;
    addFundEnabled: boolean;
    fundAmount: number;
    sandboxMode: integratedFinanceSanboxMode;
  };
  outboundPaymentAtttributePlugins?: string[];
  disableIndividualAutomaticAccountCreation?: boolean;
  supportedCurrency: string[];
  enableHeaders: boolean;
  sandboxEnabled: boolean;
  serviceProvider?: string;
  providerCurrencyAccountLimit?: ProviderLimitConfig;
  transferReasonId: string;
  checkPayeeEnabled: boolean;
  smsAuthenticationEnabled: boolean;
  beneficiaryCheckPayeeProvider?: string;
  smsAuthenticationExclusion: {
    [key: string]: string[];
  };
  debitOnSettlementExchangeEnabled?: boolean;
  // used to know if we should set balance as the IF available balance minus the pending trasnactions + excahnges
  calculatePayableBalance?: boolean;
  pricing: {
    implicit: {
      sourcingMethod: integratedFinancePricingMode;
    };
    explicit: {
      sourcingMethod: integratedFinancePricingMode;
      enabled: true;
      chargingType: string;
      chargingMethod: string;
    };
    method: integratedFinancePricingMode;
    attributes: {
      [key: string]: string | boolean | number;
    };
  };
  exchangePricing: {
    implicit: {
      sourcingMethod: integratedFinancePricingMode;
    };
    explicit: {
      sourcingMethod: integratedFinancePricingMode;
      enabled: true;
      chargingType: string;
      chargingMethod: string;
    };
    method: integratedFinancePricingMode;
    attributes: {
      [key: string]: string | boolean | number | object;
    };
  };
  genericTransactions?: {
    [key: string]: string;
  };
  feesConfig?: {
    applyFeeKey: string;
    revertFeeKey: string;
  } | null;
  alternativeBackends?: {
    [key: string]: IntegratedFinaceConfigValuesType;
  };
};

export type KycProviderService = {
  provider: string;
  sumsubConfig: {
    extraAttributes?: string[];
    createApplicantOnRegister: boolean;
    useInfoInsteadOfFixedInfo?: boolean;
  };
};

export type SystemConfiguration = {
  roles: SystemRoles;
  subscription: SystemSubscriptionPlan;
  ui: UiSystemConfig;
  pageNavigationMap?: PageNavigationMap;
  account_service?: { provider: PaymentProvider };
  packs?: PACKS[];
};

export type ProjectConfigUISetting = {
  pageTransitionPreset?: string | TransitionPresetType;
  modalTransitionPreset?: string | TransitionPresetType;
  modalTransitionCustomParams?: any;
  pageTransitionCustomParams?: any;
};

export enum DocumentProviders {
  DATABASE = 'database',
  THISBANK = 'thisbank',
}

export type DocumentProviderActionType = 'upload' | 'download' | 'delete' | 'get' | 'post_upload';

export type DocumentProviderTypeConfig = {
  default: DocumentProviders;
  actions?: Partial<Record<DocumentProviderActionType, DocumentProviders>>;
  [category: string]:
    DocumentProviders | Partial<Record<DocumentProviderActionType, DocumentProviders>> | undefined;
};

export type DocumentProviderService = {
  providers: {
    [documentAction: string]: DocumentProviderTypeConfig;
  };
  default_provider: DocumentProviders;
};

export enum ProjectTaskStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export interface IProjectTask {
  id: string;
  name: string;
  description: string;
  status: ProjectTaskStatus;
  sequence: number;
  userId?: string;
  completedAt?: Date;
  payload?: ClientOnboardingPayload | null;
}

export interface IOnboardingDashboard {
  tasks: IProjectTask[];
  completedCount: number;
  totalCount: number;
}

export type paymentCancellationSettingConfig = {
  isPaymentCancellationEnabled: boolean;
  paymentCancellationTimer: number;
};

export type PostDownloadRedirectionConfig = {
  mobileUrl: string;
  tabletUrl: string;
  timeInterval: number;
  timeIntervalUnit: TimeIntervalUnitEnum;
};

/**
 * One project's override of the Admin BFF's write policy for one catalogued operation
 * (ADR 0015: "policy is per operation and per project").
 *
 * Every field can only be written in the **tightening** direction, and the type says so:
 * `resolution` may be raised to `propose` but never lowered to `apply`, `stepUp` may be
 * turned on but never off, `idempotency` may be made `required` but never made optional,
 * and `requiredApprovals` may only be raised. The deployment's default table
 * (`ADMIN_APP_WRITE_POLICY` in account-service) is therefore the loosest a project can
 * ever be.
 *
 * That direction is the whole point: this config lives in a JSONB blob in another
 * service, and a project able to turn `payments.transfer` from `propose` into `apply`
 * would be a two-person rule removed by editing a document. The type documents the rule;
 * `resolveAdminWritePolicy` enforces it at runtime and ignores anything loosening,
 * because the config is untyped by the time it arrives.
 */
export type AdminAppWritePolicyOverride = {
  resolution?: 'propose';
  requiredApprovals?: number;
  stepUp?: true;
  idempotency?: 'required';
};

/**
 * Per-project settings for the Admin Application BFF (`admin-app/*`).
 *
 * Keyed by catalogued operation name (`accounts.rename`, `cards.freeze`, …). A key
 * naming an operation this deployment does not serve is inert.
 */
export type AdminAppProjectSettings = {
  writePolicy?: Record<string, AdminAppWritePolicyOverride>;
};

export type ProjectConfiguration = {
  system: {
    enabled_services: {
      accounts_and_payments: boolean;
    };
  };
  onboardingFlow?: OnboardingConfigFlowTypes;
  onboardingValidations?: { noDupePhoneNumbers?: boolean; allowPhoneBypass?: boolean };
  document_service: DocumentProviderService;
  account_service: AccountProviderService;
  'system-entities': {
    organisations: {
      group: {
        extended_attributes: [];
      };
      individual: {
        extended_attributes: [];
      };
    };
  };
  notification_service: NotificationProviderService | null;
  locality: string;
  kyc_provider_service: KycProviderService | null;
  kyc_regulated: boolean;
  sms_config?: {
    twilio_from_sender: string;
    verify_message_handlebar_template?: string;
    verify_ttl_minutes?: number;
    verified_contact_cache_ttl_minutes?: number;
  };
  email_config?: {
    verify_otp_expiry_minutes?: number;
    verified_contact_cache_ttl_minutes?: number;
  };
  // Probably should be moved to nested object
  keycloak_auth_url: string;
  keycloak_realm: string;
  keycloak_client_id: string;
  organisationDefaultRole: string;

  publish_url: string;
  file_storage_url: string;
  site_url: string;
  defaultCurrency: string;
  checkPayeeEnabled: boolean;
  checkPayeeConfig?: {
    checkPayeeVerifiedCacheTtlMinutes?: number;
  };
  smsAuthenticationEnabled: boolean;
  orgCheck: OrgCheckTypes;
  default_locale: Locale;
  fee_management: {
    enabled: boolean;
    retryFailedFeeEnabled: boolean;
    feeReversalEnabled: boolean;
  };
  defaultOtherAlerts?: boolean;
  multiOrganisationPaymentApproval?: boolean;
  uiSettings?: ProjectConfigUISetting;
  confirmationPreferenceOrder?: string[];
  authentication?: ProjectAuthenticationSettings;
  aispFlowRedirectionConfig?: { [key in NominatedAccountStatusEnum]: string };
  pispDepositFlowRedirectionConfig?: PispDepositFlowRedirectionConfig;
  postDownloadRedirectionConfig?: PostDownloadRedirectionConfig;
  deviceScrollbarVisibility?: DeviceScrollbarVisibility;
  deviceViewEnabled?: DeviceViewEnabled;
  personaSpecificSettings?: PersonaSpecificSettings;
  failureAlerts?: FailureAlertsConfig;
  jobScheduler?: string[];
  formProgress?: {
    formId: FormID[];
    formsConfig: {
      expiry: string;
    };
  };
  profileUpdateLockoutDays?: number;
  permittedProfileUpdateFields?: string[];
  paymentCancellationSettings?: paymentCancellationSettingConfig;
  kycStatusRoutingConfig?: KycStatusRoutingConfig | null;
  onboardingSubmissionConfig?: {
    otpExpiryTime?: string | number; // in minutes
    copCheckExpiryTime?: string | number; // in minutes
  };
  company_house_service?: CompanyCheckProviderInfoType;
  jointAccountInvitationExpiryDays?: number;
  /** Consecutive failed VRP collection attempts before the mandate is auto-cancelled (9590). */
  maxFailedVrpCollectionAttempts?: number;
  /** Per-project Admin BFF settings — today, write-policy tightenings. */
  adminApp?: AdminAppProjectSettings;
  jointAccountInvitationRedirectionConfig?: {
    [key in ActiveDevice]: {
      newUser: string;
      existingUser: string;
      expiredInvitation: string;
    };
  };
};

export type CompanyCheckProviderInfoType = {
  provider: string;
  companyHouseConfig: CompanyHouseConfigValuesType;
};

export type CompanyHouseConfigValuesType = {
  environment: 'live' | 'sandbox';
  sandboxConfig: CompanyHouseSandboxConfig;
  liveConfig: CompanyHouseLiveConfig;
};

export type CompanyHouseSandboxConfig = {
  api_key: string;
  api_url: string;
};
export type CompanyHouseLiveConfig = {
  api_key: string;
  api_url: string;
};

export type FailureAlertFeatureConfig = {
  enabled: boolean;
};

export type FailureAlertsConfig = {
  email: string;
  cc?: string[];
  feature: {
    [featureKey: string]: FailureAlertFeatureConfig;
  };
};

interface PersonaSpecificSettings {
  [key: string]: {
    authentication?: ProjectAuthenticationSettings;
    //add more project configs here as needed to be overridden per role
  };
}

export type OnboardingZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
            };
          };
        };
      };
    };
  };
};

export type ChartsZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
            };
          };
        };
      };
    };
  };
};

export type LandingZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
            };
          };
        };
      };
    };
  };
};

export type VerificationZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
              sumsublayout: LayoutWidgetConfig[];
              enabled: boolean;
              additional_configs: {
                validators: ExtraAttributeField[];
                additional_fields: ExtraAttributeField[];
              };
              kycVerficationEmail: boolean;
              kycVerficationPhone: boolean;
            };
          };
        };
      };
    };
  };
};

export type ProfileZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              additional_configs: {
                additional_fields: ExtraAttributeField[];
                validators: [];
              };
              'terms-and-condition': boolean;
              'privacy-policy': boolean;
              css_config: BaseCssConfig;
            };
          };
        };
      };
    };
  };
};

export type onboardingWidgets = {
  name: WidgetLayout;
  birthday: WidgetLayout;
  address: WidgetLayout;
  phone: WidgetLayout;
  email: WidgetLayout;
  company_details: WidgetLayout;
};

export type LandingPageConfig = {
  // defines extra info needed to be collected or performed in initial user sign up
  organisations: {
    individual: {
      showCardBalance: boolean;
      showMonthlyIncome: boolean;
      showCardBackSideDetailsIcon: boolean;
      showMonthlyExpenditure: boolean;
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
    };
    group: object;
  };
};

export type GeneralPageConfig = {
  organisations: {
    individual: {
      showCardBalance: boolean;
      showMonthlyIncome: boolean;
      showCardBackSideDetailsIcon: boolean;
      showMonthlyExpenditure: boolean;
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
    };
    group: object;
  };
};

export type SignUp = {
  additional_configs: {
    validators: ExtraAttributeField[];
    additional_fields: ExtraAttributeField[];
  };
  verificationViaOTP: boolean;
};

export interface ExtraAttributeField {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  options?: any;
  name: string;
  displayName: string;
  type: ExtraAttributeFieldType;
  validators?: ValidatorType[];
}

export interface ExtraAttributeFieldDropdown extends ExtraAttributeField {
  options: { key: string; value: string }[];
}
export type ExtraAttributeFieldTextField = ExtraAttributeField;

export enum ExtraAttributeFieldType {
  TEXTFIELD,
  DROPDOWN,
  CHECKBOX,
  RADIO,
}

export enum PageConfigSwitchType {
  PageHeader = 'pageHeader',
  LabelsAsPlaceholder = 'labelsAsPlaceholder',
  IsCustomGridActive = 'isCustomGridActive',
  IsBodyScrollingEnabled = 'isBodyScrollingEnabled',
  IsUsingHtmlStructure = 'isUsingHtmlStructure',
  IsAutoRedirectEnabled = 'isAutoRedirectEnabled',
  hideNavigationMenu = 'hideNavigationMenu',
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ValidatorType {}

export class AccountPageWidgetConfig {
  name: string;
  position: number;
  attributes: unknown;

  constructor(name: string, position: number, attributes: unknown) {
    this.name = name;
    this.position = position;
    this.attributes = attributes;
  }
}

export type LayoutWidgetConfig = {
  name: ComponentLabel;
  position: number;
  widgetIndex?: number;
  type?: string;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  attributes: any;
  uniqueId: number | string;
  // Need to remove any as a type here and move all wdgets over to grids
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  gridPosition: GridPosition | any;
  layout?: LayoutWidgetConfig[];
};

export interface CustomColourType {
  name: string;
  code: string;
} // todo: may delete this type, unless we want to extend the properties of ColourGroupFields, so use this instead.

export interface PaletteTypeColourOptions {
  primary: string;
  background: string;
  surface: string;
  borderColor: string;
  default: string;
  onPrimary: string;
  onPrimaryMuted: string;
  onBackground: string;
  onBackgroundMuted: string;
  onSurface: string;
  onSurfaceMuted: string;
  active: string;
  hover: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

export type ColourGroupFields = Record<string, string>;

/**
 * Top-level `NewColourPaletteType` keys that are NOT user-defined custom
 * groups. A custom group is any other top-level key: a flat `{ [colourName]:
 * hex }` object, structurally identical to `main`/`border`/etc. Their fixed
 * colour keys (e.g. `borderColor`, `onPrimaryMuted`) are stored with this
 * exact case, but `getPaletteCssVariableName` slugifies them into kebab-case
 * (e.g. `--color-main-border-color`) the same as custom groups.
 */
export const RESERVED_PALETTE_GROUP_NAMES = [
  'main',
  'border',
  'states',
  'status',
  'typography',
  'customColors',
] as const;

export interface NewColourPaletteType {
  main: ColourGroupFields;
  border: ColourGroupFields;
  typography: ColourGroupFields;
  states: ColourGroupFields;
  status: ColourGroupFields;
  customColors: CustomColourType[]; // legacy flat list, kept as-is for backwards compatibility
  [groupName: string]: ColourGroupFields | CustomColourType[]; // extension point — new groups just appear here
}

export type ButtonSizeConfigurations = {
  'padding-top': string;
  'padding-right': string;
  'padding-bottom': string;
  'padding-left': string;
  'margin-top': string;
  'margin-right': string;
  'margin-bottom': string;
  'margin-left': string;
  heightConfig?: ButtonHeightConfig;
  paddingWithIcon?: {
    gap: string;
    'padding-right': string;
    'padding-left': string;
  };
};
export type ButtonHeightConfig = {
  height: string;
  heightType: ButtonHeightSizeOptions;
  heightUnit: string;
};
export enum InputLabelOptions {
  STATIC = 'static',
  PLACEHOLDER = 'placeholder',
  FLOATING = 'floating',
}

export enum ButtonSizeOptions {
  SMALL = 'small',
  LARGE = 'large',
  FULL_WIDTH = 'full-width',
}

export type ButtonAndInputStatesConfig = {
  textColor: {
    color: string;
  };
  labelColor: {
    color: string;
  };
  'border-color': string;
  'background-color': string;
};

export type TextSkeletonType = Pick<ButtonAndInputStatesConfig, 'background-color'> & BorderRadiusStyles;

export type ButtonConfigBaseStyles = {
  'justify-content': string;
  'align-items': string;
  'border-style': string;
  'box-shadow': string;
  'border-top-left-radius': string;
  'border-top-right-radius': string;
  'border-bottom-right-radius': string;
  'border-bottom-left-radius': string;
  'border-top-width': string;
  'border-left-width': string;
  'border-bottom-width': string;
  'border-right-width': string;
};

export type BorderRadiusStyles = Pick<
  ButtonConfigBaseStyles,
  | 'border-top-left-radius'
  | 'border-top-right-radius'
  | 'border-bottom-right-radius'
  | 'border-bottom-left-radius'
>;
export type ButtonIconConfigurations = {
  type: 'left' | 'right';
  iconName: string;
  height: string;
  width: string;
  color: string;
};

export enum ButtonLoadingIndicatorType {
  TEXT = 'text',
  ANIMATION = 'animation',
}

export type ButtonLoadingConfig = {
  indicatorType: ButtonLoadingIndicatorType;
  // Only meaningful when indicatorType is TEXT. Colour is not configurable here -
  // it always inherits the button's own text colour via `currentColor`.
  loadingText?: string;
};

export type ButtonConfigurations = {
  idle: Omit<ButtonAndInputStatesConfig, 'labelColor'>;
  active: Omit<ButtonAndInputStatesConfig, 'labelColor'>;
  hover: Omit<ButtonAndInputStatesConfig, 'labelColor'>;
  loading: ButtonLoadingConfig;
  baseStyles: ButtonConfigBaseStyles;
  size: {
    [ButtonSizeOptions.SMALL]: ButtonSizeConfigurations;
    [ButtonSizeOptions.LARGE]: ButtonSizeConfigurations;
    [ButtonSizeOptions.FULL_WIDTH]: ButtonSizeConfigurations;
  };
  borderRadiusClass: BorderRadiusKeys;
  iconConfigurations?: ButtonIconConfigurations;
  isLinkPrimaryButtonConfig?: boolean;
};

export type TypographyStyleConfigurations = {
  'font-family': string;
  'font-weight': string;
  'text-decoration': string;
  'font-size': string;
  'letter-spacing': string;
  'line-height': string;
  'margin-bottom': string;
};

export interface TypographyStyleConfigurationsForm {
  'font-weight': FormControl<string>;
  'text-decoration': FormControl<string>;
  'font-size': FormControl<string>;
  'letter-spacing': FormControl<string>;
  'line-height': FormControl<string>;
  'margin-bottom': FormControl<string>;
}

export enum ButtonTypes {
  PRIMARY = 'primaryButton',
  SECONDARY = 'secondaryButton',
  TERTIARY = 'tertiaryButton',
  MENU = 'menuButton',
}

export type InputBaseStyles = {
  'align-items': string;
  'justify-content': string;
  'padding-top': string;
  'padding-right': string;
  'padding-bottom': string;
  'padding-left': string;
  height: string;
  width: string;
  'border-top-left-radius': string;
  'border-top-right-radius': string;
  'border-bottom-right-radius': string;
  'border-bottom-left-radius': string;
  'border-top-width': string;
  'border-left-width': string;
  'border-bottom-width': string;
  'border-right-width': string;
  'box-shadow': string;
  'border-style': string;
};

export type InputLabelConfig = {
  type: InputLabelOptions;
  'align-items': string;
  'justify-content': string;
  'padding-top': string;
  'padding-right': string;
  'padding-bottom': string;
  'padding-left': string;
};

export type OtpInputFieldSpacingConfig = {
  'margin-right': string;
};

export interface InputFieldsConfigurations {
  idle: ButtonAndInputStatesConfig;
  active: ButtonAndInputStatesConfig;
  labelConfig: InputLabelConfig;
  inputStyles: InputBaseStyles;
  textStyleClass: string;
  borderRadiusClass: BorderRadiusKeys;
  otpInputFieldSpacing: OtpInputFieldSpacingConfig;
  finxForm: { gap: string };
}

export interface TagColorConfig {
  idle: ButtonAndInputStatesConfig;
  active: ButtonAndInputStatesConfig;
}
export interface GlobalThemeConfiguration {
  themeName: string;
  generalConfigurations: {
    companyInfo: {
      name: string;
      legalEntityName: string;
      logo: string;
      logoTitle: string;
      logoFileName: string;
    };
    defaultSystemFont?: boolean; // making it optional because its not present in some old themes and will cause issues while importing themes (even those we just exported).
    fontFamily: string;
    secondaryFontFamily?: string;
    palette: NewColourPaletteType;
    extraCss?: string;
    loaderCss?: string;
  };
  buttons: {
    [ButtonTypes.PRIMARY]: ButtonConfigurations;
    [ButtonTypes.SECONDARY]: Pick<
      ButtonConfigurations,
      'idle' | 'active' | 'hover' | 'loading' | 'baseStyles' | 'borderRadiusClass'
    >;
    [ButtonTypes.TERTIARY]: Pick<
      ButtonConfigurations,
      'idle' | 'active' | 'hover' | 'loading' | 'baseStyles' | 'borderRadiusClass'
    >;
    [ButtonTypes.MENU]: ButtonConfigurations;
  };
  typography: {
    [key in TypographyKeys]: TypographyStyleConfigurations;
  };
  inputFields: InputFieldsConfigurations;
  tooltipConfigurations: TooltipStyleConfigurations;
  confirmationDialogConfigurations: ConfirmationDialogStyleConfigurations;
  radius: {
    [key in BorderRadiusKeys]: BorderRadiusStyles;
  };
  textSkeleton: {
    [key in TypographyKeys]: TextSkeletonType;
  };
}

export type TooltipStyleConfigurations = Omit<InputBaseStyles, 'height' | 'width'> & {
  textStyleClass: string;
  borderRadiusClass: BorderRadiusKeys;
  'background-color': string;
  'border-color': string;
  color: string;
};

export type ConfirmationDialogStyleConfigurations = {
  'background-color': string;
  'border-color': string;
  borderRadiusClass: BorderRadiusKeys;
  'border-top-left-radius': string;
  'border-top-right-radius': string;
  'border-bottom-right-radius': string;
  'border-bottom-left-radius': string;
  'border-top-width': string;
  'border-right-width': string;
  'border-bottom-width': string;
  'border-left-width': string;
  'border-style': string;
  'box-shadow': string;
  titleTextStyleClass: string;
  bodyTextStyleClass: string;
  titleColor: string;
  bodyColor: string;
  positiveButtonClass: string;
  negativeButtonClass: string;
};

export interface CompanyInfo {
  name: string;
  legalEntityName: string;
  logo: string;
  logoTitle: string;
  logoFileName: string;
}

export type CarouselWidgetPage = {
  organisations: {
    individual: {
      additional_configs: {
        additional_fields: ExtraAttributeField[];
        validators: [];
      };
      layout: LayoutWidgetConfig[];
    };
  };
};

export type UerManagementZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
            };
          };
        };
      };
    };
  };
};

export type MenuZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
            };
          };
        };
      };
    };
  };
  title?: string;
};

export type CardManagementZone = {
  roles: {
    [key: string]: {
      [key: string]: {
        [key: string]: {
          organisations: {
            group: Record<string, never>;
            individual: {
              layout: LayoutWidgetConfig[];
            };
          };
        };
      };
    };
  };
  title?: string;
};

export enum FieldName {
  VIEW_CARD_OPTION = 'viewCardOption',
  VIEW_CARD_SEPARATELY = 'ViewCardSeparately',
  CARD_MANAGEMENT_TITLE = 'Card Management',
  CARD_DETAILS_TITLE = 'Card details',
}

export type DeviceData = {
  deviceType: SupportedDevice;
  deviceWidth: number;
  deviceHeight: number;
  numberCols: number;
  gridCols: number;
  gridRows: number;
  gridster: boolean;
};

export type WidgetSection = {
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  attributes: any;
  name: string;
  position: number;
  widgetIndex: number;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  defaultBeneficiaryList?: any[];
};
export type PageWidgetConfig = {
  name: ComponentLabel;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  attributes: any;
  layout?: LayoutWidgetConfig[];
};

export enum ComponentNames {
  MONTHLYINCOME = 'monthlyIncome',
  MONTHLYSPEND = 'monthlySpend',
  SIMPLEBALANCE = 'simpleBalance',
  MONTHLYBALANCE = 'monthlyBalance',
  BALANCECHART = 'balanceChart',
  VIRTUALCARD = 'virtualCard',
  QUICKLINK = 'quickLinks',
  PAYMENTBUTTON = 'paymentButton',
  FOOTERNAV = 'footerNav',
  POPUPSELECTOR = 'popupSelector',
  AMOUNTSUMMARY = 'ammountSummaryComponent',
  CREDITDEBITSUMMARY = 'creditdebitSummaryComponent',
}

export type ProtectedProjectConfig = {
  kyc_regulated: boolean;
  provider: string;
  sandboxMode: integratedFinanceSanboxMode | string;
  checkPayeeEnabled: boolean;
  supportedCurrency: string[];
  enableHeaders: boolean;
  bankProvider?: string;
  qrescendoInvestmentEnabled?: boolean;
  cardsConfig?: CardConfigValuesType;
  feeManagement?: {
    enabled: boolean;
    retryFailedFeeEnabled: boolean;
    feeReversalEnabled: boolean;
  };
  currencyExchange?: boolean;
  paymentRequest: accountServicePaymentRequest;
  onboardingFlow: OnboardingConfigFlowTypes;
  defaultCurrency: string;
  smsAuthenticationEnabled: boolean;
  smsAuthenticationExclusion: {
    [key: string]: string[];
  };
  enableBeneficiaryFirstAndLastName: boolean;
  defaultLocale: string;
  defaultOtherAlerts: boolean;
  multiOrganisationPaymentApproval: boolean;
  uiSettings?: ProjectConfigUISetting;
  confirmationPreferenceOrder?: string[] | null;
  authentication?: ProjectAuthenticationSettings;
  deviceScrollbarVisibility?: DeviceScrollbarVisibility;
  cardsEnabled: boolean;
  supportCorporateClients: boolean;
  supportIndividualClients: boolean;
  kycProvider: string;
  paymentSchemes: string[];
  directClientCreation: boolean;
  integratedFinanceConfig?: {
    genericTransactions?:
      | {
          [key: string]: string;
        }
      | undefined;
  };
  projectConfig: ProjectConfiguration;
};

export const defaultZoneConfiguration = {
  zonePageType: 'singlePage',
  zoneType: ZONETYPEOPTION.DEFAULT,
} as ZoneConfiguration;

export type DeviceLocalData = {
  deviceId?: string;
  manufacturer?: string;
  os?: string;
  deviceModel?: string;
};

export type State = {
  name: string;
  state_code: string;
};

export type accountServicePaymentRequest = {
  enabled: boolean;
  paymentApproverRoles: string[];
  paymentRequestorRoles: string[];
  paymentInitiatorRoles: string[];
  paymentReaderRoles: string[];
};

export enum TemplateType {
  DROPDOWN = 'dropdown',
  LIST_VIEW = 'list-view',
}

export type NavigationOptionsType = {
  navigationOption: string;
  zone: string;
  pageUrl: string;
  pageId: string;
  pageTransition: string;
  role: string;
  tabRef: string;
  externalUrl?: string;
  browserBehaviour?: string;
};

export enum TransactionFilterOptions {
  DATE = 'date',
  STATUS = 'status',
  TYPE = 'type',
  CATEGORY = 'category',
  CURRENCY = 'currency',
  TAGS = 'tags',
}

export type ScaPolicyMode = 'permissive' | 'required';

/**
 * Per-project strong-customer-authentication posture (Device Trust Stage A, decision D7).
 * `permissive` admits a confirmation-gated request that carries no confirmation — the
 * platform's historical behaviour. `required` refuses it (fail closed). A claim named in
 * `claims` wins over `default`; a claim named nowhere falls back to the platform-wide
 * compile-time posture, so a project that declares nothing behaves exactly as before.
 */
export type ScaPolicy = {
  default?: ScaPolicyMode;
  /** Keyed by confirmation claim name (`deviceEnrollment`, `addBeneficiary`, `makePayment`, ...). */
  claims?: Partial<Record<string, ScaPolicyMode>>;
};

export type ProjectAuthenticationSettings = {
  login: {
    confirmationPreferenceOrder: ConfirmationPreferenceTypes[];
  };
  inApp: {
    confirmationPreferenceOrder: ConfirmationPreferenceTypes[];
    beneficiaryConfirmationEnabled: boolean;
  };
  /** Read only from the project-level block, never from a persona override. */
  scaPolicy?: ScaPolicy;
};

export type DeviceScrollbarVisibility = {
  mobile: boolean;
  tablet: boolean;
  desktop: boolean;
};

export type DeviceViewEnabled = {
  mobile: boolean;
  tablet: boolean;
  desktop: boolean;
};

export type DataDefinitionShape = 'document' | 'collection';

/** One declared access pattern of a collection: an ordered list of field paths in `value`. */
export interface DataCollectionIndex {
  fields: string[];
}

/**
 * V1 collection limits (ADR 0022, James 2026-09-06). Enforced by account-service on the data
 * plane and by the provisioner at definition publish; the builder mirrors them for copy only.
 */
export const DATA_COLLECTION_LIMITS = {
  /** Records per collection per organisation. */
  maxRecordsPerCollection: 100_000,
  /** Records one query may return; the default page when the caller names none. */
  maxQueryResults: 200,
  defaultQueryResults: 50,
  /** Declared indexes per collection. */
  maxIndexes: 5,
  /** Fields per index. */
  maxIndexFields: 3,
  /** Values one `in` filter may carry. */
  maxInValues: 20,
  /** Bytes per record value, the same cap the document path applies. */
  maxRecordBytes: 64 * 1024,
} as const;
